import React, { useState, useEffect, useRef } from "react";
import { Upload, FileText, Clock, Sparkles, Mail, Package, ArrowRight, Pause, Play, Tag, MessageSquare, ClipboardCheck, Home } from "lucide-react";

const STEPS = [
 {
 title: "Upload anything",
 body: "Drop in a photo, an email, a PDF. Here, the tenant uploads kitchen_leak.jpg, a phone photo taken at 22:08 on 14 March. The EXIF date proves when it actually happened.",
 Icon: Upload,
 },
 {
 title: "Annexed automatically",
 body: "Every document gets a permanent Annex number based on when it actually happened. Drag-order chaos becomes a clean numbered record.",
 Icon: FileText,
 },
 {
 title: "Placed on the chronology",
 body: "The same document slots onto your visual timeline at the right date. The order of events is half the battle at tribunal.",
 Icon: Clock,
 },
 {
 title: "Compass reads it",
 body: "Ask anything. Our AI has read every annex and the relevant state's Residential Tenancies Act. Every Annex N and section reference is a clickable link.",
 Icon: Sparkles,
 },
 {
 title: "Referenced in your letters",
 body: "Pick a template, Repairs, Bond Dispute, Notice and the AI drafts a polished letter citing the right Act sections and pointing back to your annexes.",
 Icon: Mail,
 },
 {
 title: "Bundled for tribunal",
 body: "One click. Cover page, indexed contents, every document appended in order. Take it straight to QCAT, NCAT, VCAT, wherever you're heading.",
 Icon: Package,
 },
];

const STEP_MS = 5500;

export default function FeatureShowcase() {
 const [active, setActive] = useState(0);
 const [paused, setPaused] = useState(false);
 const timerRef = useRef(null);

 useEffect(() => {
 if (paused) return;
 timerRef.current = setTimeout(() => setActive((a) => (a + 1) % STEPS.length), STEP_MS);
 return () => clearTimeout(timerRef.current);
 }, [active, paused]);

 // Visible: things that have been "revealed" up to and including the current step
 const reached = (i) => active >= i;
 const isCurrent = (i) => active === i;

 return (
 <section className="bg-[#FAF9F6] border-y border-[#E5E4DE]">
 <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="feature-showcase"
 onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
 <div className="mb-12">
 <div className="label-mono mb-3 font-bold !text-[#0D2B52] text-[11.5px] sm:text-[12.5px]">Watch the trail</div>
 <h2 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] max-w-xl leading-[1.15]">One document. Six stops. End-to-end evidence.</h2>
 <p className="text-[#6B7280] max-w-xl mt-3 text-sm">Follow one photo, <span className="font-mono text-[#0D2B52]">kitchen_leak.jpg</span>, through TenantTrail, all the way from your phone to a tribunal-ready PDF.</p>
 </div>

 <div className="grid lg:grid-cols-12 gap-10">
 {/* LEFT: stepper list */}
 <div className="lg:col-span-5">
 <ol className="space-y-2">
 {STEPS.map((s, i) => {
 const cur = isCurrent(i);
 const done = reached(i);
 return (
 <li key={i}>
 <button
 onClick={() => { setActive(i); }}
 className={`w-full text-left p-4 border-l-4 transition-all duration-300 ${cur ? "border-[#1B6C62] bg-white" : done ? "border-[#E5E4DE] bg-[#FAF9F6]" : "border-transparent bg-transparent"}`}
 data-testid={`step-${i}`}
 >
 <div className="flex items-start gap-3">
 <div className={`w-7 h-7 flex items-center justify-center text-xs font-mono flex-shrink-0 ${cur ? "bg-[#0D2B52] text-white" : done ? "bg-[#1B6C62]/15 text-[#1B6C62]" : "bg-white border border-[#E5E4DE] text-[#6B7280]"}`}>
 {i + 1}
 </div>
 <div className="min-w-0 flex-1">
 <div className={`font-serif text-lg ${cur ? "text-[#0D2B52]" : "text-[#6B7280]"}`}>{s.title}</div>
 {cur && <p className="text-sm text-[#333] leading-relaxed mt-1">{s.body}</p>}
 </div>
 </div>
 {cur && !paused && (
 <div className="mt-3 h-0.5 bg-[#E5E4DE] overflow-hidden">
 <div className="h-full bg-[#1B6C62]" style={{ width: "100%", animation: `tt-bar ${STEP_MS}ms linear forwards` }} />
 </div>
 )}
 </button>
 </li>
 );
 })}
 </ol>

 <div className="flex items-center justify-between mt-5">
 <button onClick={() => setPaused(p => !p)} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="pause-toggle">
 {paused ? (<><Play size={12} strokeWidth={1.5} /> Play</>) : (<><Pause size={12} strokeWidth={1.5} /> Pause</>)}
 </button>
 <a href="/register" className="btn-primary inline-flex items-center gap-2 text-sm">
 Start free <ArrowRight size={14} strokeWidth={1.5} />
 </a>
 </div>
 </div>

 {/* RIGHT: animated scene */}
 <div className="lg:col-span-7">
 <div className="relative bg-white border border-[#E5E4DE] rounded-sm p-6 h-full min-h-[480px] overflow-hidden">
 <style>{`@keyframes tt-bar { from { width: 0% } to { width: 100% } } @keyframes tt-pop { 0%{opacity:0; transform: translateY(8px) scale(.96);} 100%{opacity:1; transform: none;} }`}</style>

 {/* Step 1. phone upload */}
 <div className={`transition-all duration-700 ${active === 0 ? "opacity-100" : "opacity-30"}`}>
 <div className="flex justify-center mb-6">
 <div className="w-56 border-2 border-dashed border-[#1B6C62] bg-[#E6F5F2]/40 p-2 text-center">
 <img
 src="https://customer-assets.emergentagent.com/job_emergent-difference-1/artifacts/pzif0vs4_kitchen%20sink%20leak.png"
 alt="Kitchen sink leak"
 className="w-full h-24 object-cover mb-2"
 />
 <div className="text-xs font-mono text-[#0D2B52]">kitchen_leak.jpg</div>
 <div className="text-[10px] text-[#6B7280]">EXIF: 2026-03-14 22:08</div>
 </div>
 </div>
 </div>

 {/* Step 2. annexed card */}
 {reached(1) && (
 <div className="border border-[#E5E4DE] p-3 mb-4 flex gap-3 items-center" style={{ animation: "tt-pop.5s ease-out both" }}>
 <div className="w-11 h-11 flex flex-col items-center justify-center bg-[#FAF9F6] border border-[#E5E4DE]">
 <span className="font-mono text-[8px] text-[#1B6C62] tracking-wider">ANNEX</span>
 <span className="font-serif text-base text-[#0D2B52]">4</span>
 </div>
 <div className="flex-1 min-w-0">
 <div className="label-mono">PHOTO · 2026-03-14</div>
 <div className="text-sm text-[#1A1A1A] font-medium truncate">kitchen_leak.jpg</div>
 </div>
 </div>
 )}

 {/* Step 3. chronology dot */}
 {reached(2) && (
 <div className="border border-[#E5E4DE] p-3 mb-4 relative pl-7" style={{ animation: "tt-pop.5s ease-out both" }}>
 <div className="absolute left-3 top-0 bottom-0 w-px bg-[#E5E4DE]" />
 {[
 { d: "2025-08-01", t: "Lease signed" },
 { d: "2026-03-12", t: "Reported leak to agent" },
 { d: "2026-03-14", t: "kitchen_leak.jpg taken", hl: true },
 ].map((e, i) => (
 <div key={i} className="relative pl-2 py-1.5">
 <div className={`absolute -left-[14px] top-2.5 w-[9px] h-[9px] rounded-full ring-2 ring-white ${e.hl ? "bg-[#1B6C62]" : "bg-[#0D2B52]"}`} />
 <div className="font-mono text-[10px] text-[#6B7280]">{e.d}</div>
 <div className={`text-xs ${e.hl ? "text-[#0D2B52] font-medium" : "text-[#6B7280]"}`}>{e.t}</div>
 </div>
 ))}
 </div>
 )}

 {/* Step 4. copilot citation */}
 {reached(3) && (
 <div className="bg-[#E6F5F2] border border-[#1B6C62]/25 p-3 mb-4 text-[13px] font-serif text-[#1A1A1A]" style={{ animation: "tt-pop.5s ease-out both" }}>
 <div className="label-mono mb-1 text-[#0D2B52]">Compass</div>
 Yes, under <a className="text-[#1B6C62] underline">s 217 RTRA Act 2008 QLD</a>, the lessor must remedy the leak. <a className="text-[#1B6C62] underline">Annex 4</a> (kitchen_leak.jpg) shows the leak photographed on 14 March.
 </div>
 )}

 {/* Step 5. letter snippet */}
 {reached(4) && (
 <div className="bg-[#FAF9F6] border border-[#E5E4DE] p-3 mb-4 font-serif text-[11.5px] leading-relaxed" style={{ animation: "tt-pop.5s ease-out both" }}>
 <div className="label-mono mb-1">DRAFT · NOTICE TO REMEDY BREACH</div>
 Pursuant to <a className="text-[#1B6C62] underline">s 217 RTRA Act 2008 (QLD)</a>, you are hereby required to remedy the leak documented in <a className="text-[#1B6C62] underline">Annex 4</a> (kitchen_leak.jpg)...
 </div>
 )}

 {/* Step 6. bundle */}
 {reached(5) && (
 <div className="border border-[#E5E4DE] bg-white p-3" style={{ animation: "tt-pop.5s ease-out both" }}>
 <div className="flex justify-between items-center mb-2">
 <div>
 <div className="font-serif text-sm text-[#0D2B52]">Tenancy Evidence Bundle.pdf</div>
 <div className="font-mono text-[10px] text-[#6B7280]">Cover · TOC · Annex 1 → Annex 4 (incl. kitchen_leak.jpg)</div>
 </div>
 <Package size={20} strokeWidth={1.5} className="text-[#1B6C62]" />
 </div>
 <div className="text-[10px] text-center text-[#6B7280] italic">↳ Ready for QCAT submission</div>
 </div>
 )}
 </div>
 </div>

 {/* Since-then strip: recent power features that live on top of the six-stop core. */}
 <div className="lg:col-span-12 mt-16 pt-10 border-t border-[#E5E4DE]" data-testid="feature-showcase-since-then">
 <div className="mb-6">
 <div className="label-mono mb-2 font-bold !text-[#0D2B52] text-[11.5px] sm:text-[12.5px]">Going deeper</div>
 <h3 className="font-serif text-2xl sm:text-3xl text-[#0D2B52] leading-[1.2] max-w-xl">
 The six stops are just the spine. These sit on top.
 </h3>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
 {[
 {
 Icon: Tag,
 title: "Colored tags",
 body: "Group any document under a colored tag: Carpet issues, Water leaks, Bond dispute. Filter the vault to one tag with a click. Export just those docs as their own tribunal-ready bundle.",
 testid: "since-then-tags",
 },
 {
 Icon: MessageSquare,
 title: "Compass by tag",
 body: "Point Compass at a tag before you ask. It answers grounded in just those documents, so the citations track back to the exact evidence you flagged.",
 testid: "since-then-compass-tag",
 },
 {
 Icon: ClipboardCheck,
 title: "Save AI answers as evidence",
 body: "Every Compass reply has a Save this answer as evidence button. One click turns the question + answer + citations into a timestamped PDF filed under your case.",
 testid: "since-then-save-evidence",
 },
 {
 Icon: Home,
 title: "Move-Out Mode",
 body: "State-aware checks against your entry report, exit report and bond claim. Flags questionable deductions with the exact Act section. Deduction Fairness Check estimates fair depreciated value.",
 testid: "since-then-moveout",
 },
 ].map(({ Icon, title, body, testid }) => (
 <div
 key={testid}
 className="bg-white border border-[#E5E4DE] p-5 hover:border-[#1B6C62]/40 transition-colors"
 data-testid={testid}
 >
 <div className="w-9 h-9 border border-[#1B6C62]/40 bg-[#1B6C62]/5 flex items-center justify-center mb-3">
 <Icon size={17} strokeWidth={1.75} className="text-[#1B6C62]" />
 </div>
 <div className="font-serif text-[15.5px] text-[#0D2B52] leading-snug mb-1.5">{title}</div>
 <p className="text-[12.5px] text-[#4a5b6c] leading-[1.6]">{body}</p>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>
 </section>
 );
}
